/**
 * BusinessInfoTab Component
 * Configure business information: name, address, contact, and payment methods
 */

import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Spinner } from 'react-bootstrap';
import {
  IconBuilding,
  IconPhone,
  IconMail,
  IconWorld,
  IconCreditCard,
  IconClock,
  IconCheck,
} from '@tabler/icons-react';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import { BRAZILIAN_STATES, PAYMENT_METHODS } from '../../constants';
import { api } from '../../../../api/Middleware';
import { BUSINESS_HOURS_SETTINGS_URL } from '../../../../api/Urls';
import './BusinessInfoTab.css';

// ===============================================
// PAYMENT METHODS SELECTOR
// ===============================================

const PaymentMethodsSelector = ({ selected, onChange }) => {
  const toggleMethod = (methodId) => {
    if (selected.includes(methodId)) {
      onChange(selected.filter(m => m !== methodId));
    } else {
      onChange([...selected, methodId]);
    }
  };

  return (
    <div className="payment-methods-grid">
      {PAYMENT_METHODS.map((method) => {
        const isSelected = selected.includes(method.id);
        return (
          <div
            key={method.id}
            className={`payment-method-card ${isSelected ? 'selected' : ''}`}
            onClick={() => toggleMethod(method.id)}
          >
            <span className="method-name">{method.name}</span>
          </div>
        );
      })}
    </div>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const BusinessInfoTab = ({ agentId }) => {
  const { state, updateBusinessInfo } = useAgentsFlow();
  const { editor } = state;

  const [config, setConfig] = useState({
    business_name: '',
    address: '',
    address_number: '',
    address_complement: '',
    city: '',
    state: '',
    phone: '',
    email: '',
    website: '',
    payment_methods: [],
  });

  // Load existing config
  useEffect(() => {
    if (editor?.business_info) {
      setConfig({
        business_name: editor.business_info.business_name || '',
        address: editor.business_info.address || '',
        address_number: editor.business_info.address_number || '',
        address_complement: editor.business_info.address_complement || '',
        city: editor.business_info.city || '',
        state: editor.business_info.state || '',
        phone: editor.business_info.phone || '',
        email: editor.business_info.email || '',
        website: editor.business_info.website || '',
        payment_methods: editor.business_info.payment_methods || [],
      });
    }
  }, [editor?.business_info]);

  // Handle change
  const handleChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    updateBusinessInfo(newConfig);
  };

  // -------- Slot global (BusinessHoursSettings) --------
  // Carregamento e salvamento próprios — fora do fluxo do contexto editor,
  // pois esse modelo vive em outra tabela com endpoint dedicado.
  const [bhsLoading, setBhsLoading] = useState(true);
  const [bhsId, setBhsId] = useState(null);
  const [bhsDuration, setBhsDuration] = useState('30');
  const [bhsSaving, setBhsSaving] = useState(false);
  const [bhsSaved, setBhsSaved] = useState(false);
  const [bhsError, setBhsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBhsLoading(true);
    api.get(BUSINESS_HOURS_SETTINGS_URL)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.results?.[0] || res.data?.[0] || res.data;
        if (data && typeof data === 'object' && data.appointment_duration != null) {
          setBhsId(data.id || null);
          setBhsDuration(String(data.appointment_duration));
        }
      })
      .catch(() => { /* mantém default */ })
      .finally(() => { if (!cancelled) setBhsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSaveBhs = async () => {
    setBhsSaving(true);
    setBhsError(null);
    setBhsSaved(false);
    try {
      const parsed = Math.max(1, parseInt(bhsDuration, 10) || 30);
      const payload = { appointment_duration: parsed };
      let response;
      if (bhsId) {
        response = await api.patch(`${BUSINESS_HOURS_SETTINGS_URL}${bhsId}/`, payload);
      } else {
        response = await api.post(BUSINESS_HOURS_SETTINGS_URL, payload);
      }
      const saved = response.data;
      if (saved?.id) setBhsId(saved.id);
      if (saved?.appointment_duration != null) {
        setBhsDuration(String(saved.appointment_duration));
      }
      setBhsSaved(true);
      setTimeout(() => setBhsSaved(false), 2500);
    } catch (err) {
      const data = err?.response?.data;
      const msg = data?.detail
        || (typeof data === 'string' ? data : null)
        || (data && typeof data === 'object' ? Object.values(data).flat()[0] : null)
        || 'Erro ao salvar';
      setBhsError(msg);
    } finally {
      setBhsSaving(false);
    }
  };

  return (
    <div className="tab-content-wrapper">
      {/* Basic Info */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <IconBuilding size={20} />
            Informações do Negócio
          </h6>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nome do Negócio</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: Clínica Saúde e Vida"
              value={config.business_name}
              onChange={(e) => handleChange('business_name', e.target.value)}
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Endereço</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: Rua das Flores"
                  value={config.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Número</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="123"
                  value={config.address_number}
                  onChange={(e) => handleChange('address_number', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Complemento</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Sala 10, Bloco B"
                  value={config.address_complement}
                  onChange={(e) => handleChange('address_complement', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cidade</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: São Paulo"
                  value={config.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={config.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {BRAZILIAN_STATES.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Contact Info */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <IconPhone size={20} />
            Informações de Contato
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <IconPhone size={16} className="me-2" />
                  Telefone
                </Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={config.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <IconMail size={16} className="me-2" />
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="contato@empresa.com.br"
                  value={config.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>
              <IconWorld size={16} className="me-2" />
              Website
            </Form.Label>
            <Form.Control
              type="url"
              placeholder="https://www.suaempresa.com.br"
              value={config.website}
              onChange={(e) => handleChange('website', e.target.value)}
            />
          </Form.Group>
        </Card.Body>
      </Card>

      {/* Payment Methods */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <IconCreditCard size={20} />
            Formas de Pagamento Aceitas
          </h6>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-3">
            Selecione as formas de pagamento aceitas. <strong>Estes dados serão inseridos automaticamente no prompt do agente.</strong>
          </p>
          <PaymentMethodsSelector
            selected={config.payment_methods}
            onChange={(methods) => handleChange('payment_methods', methods)}
          />
        </Card.Body>
      </Card>

      {/* Scheduling Settings (global) */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <IconClock size={20} />
            Configurações de Agendamento
          </h6>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-3">
            Duração padrão de cada slot de agendamento. <strong>Afeta a grade visual do calendário (FullCalendar).</strong> Para que o agente sugira horários em outro ritmo numa agenda específica, ajuste pelo botão <em>"Configurar agenda"</em> dentro do nó de Agendamento.
          </p>
          {bhsLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" />
              <span>Carregando…</span>
            </div>
          ) : (
            <Row className="align-items-end">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Duração do slot (minutos)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="5"
                    value={bhsDuration}
                    onChange={(e) => setBhsDuration(e.target.value)}
                    disabled={bhsSaving}
                  />
                </Form.Group>
              </Col>
              <Col md="auto">
                <button
                  type="button"
                  onClick={handleSaveBhs}
                  disabled={bhsSaving}
                  className="btn btn-primary"
                >
                  {bhsSaving ? (
                    <><Spinner animation="border" size="sm" /> Salvando…</>
                  ) : bhsSaved ? (
                    <><IconCheck size={14} /> Salvo</>
                  ) : (
                    'Salvar'
                  )}
                </button>
              </Col>
            </Row>
          )}
          {bhsError && (
            <p className="text-danger mt-2 mb-0" style={{ fontSize: 13 }}>{bhsError}</p>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default BusinessInfoTab;
